import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import { peutNdf } from '../../types/ndf';
import type { RefValeur, Salarie, StatutCompte, UtilisateurCourt } from '../../types/ndf';
import {
  dateFr,
  BoutonPrincipal,
  BoutonSecondaire,
  Case,
  Champ,
  Etiquette,
  Saisie,
  SaisieNombre,
  Section,
  Selection,
  ValeurCalculee,
} from '../../components/ndf/ui/Champ';
import {
  erreurGlobale,
  erreursParChamp,
  messageSiAucunChampAffiche,
  type ErreursChamps,
} from '../../components/ndf/ui/erreurs-champs';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { BarreApercu } from '../../components/ndf/ui/BarreApercu';
import { VehiculesSalarie } from '../../components/ndf/VehiculesSalarie';
import { CHAMPS_MODIFIABLES, corpsModifiable } from '../../lib/champs-salarie';
import { useToast } from '../../context/ToastContext';
import { apercuActif } from '../../services/apercu';
import { ArretsTravail } from '../../components/conges/ArretsTravail';

/**
 * Écran d'administration des fiches salariés.
 *
 * Deux notions distinctes, et c'est le point que l'écran doit rendre évident,
 * parce que le confondre a déjà coûté une reprise du modèle :
 *   — le PROFIL dit ce que la personne peut FAIRE ;
 *   — le RÔLE dit quels enregistrements elle VOIT.
 */

function nomComplet(s: Salarie) {
  return `${s.prenom} ${s.nom}`.trim();
}


/**
 * Cycle de vie du COMPTE, pas de la fiche.
 *
 * La fiche salarié est aussi le compte utilisateur : un simple « actif /
 * inactif » ne disait pas si l'accès avait seulement été ouvert. Une fiche
 * pouvait s'afficher « active » pour quelqu'un qui ne pouvait pas se connecter.
 */
const STATUTS: Record<
  StatutCompte,
  { libelle: string; ton: 'neutre' | 'actif' | 'positif' | 'alerte' | 'attention'; aide: string }
> = {
  cree: {
    libelle: 'Créé',
    ton: 'neutre',
    aide: 'La fiche existe, mais aucun accès n’a encore été ouvert : cette personne ne peut pas se connecter.',
  },
  invite: {
    libelle: 'Invité',
    ton: 'attention',
    aide: 'L’accès est ouvert. Le statut passera à « Actif » à la première connexion.',
  },
  actif: {
    libelle: 'Actif',
    ton: 'positif',
    aide: 'Cette personne s’est déjà connectée et utilise l’application.',
  },
  suspendu: {
    libelle: 'Suspendu',
    ton: 'attention',
    aide: 'Accès coupé temporairement. Les données sont conservées.',
  },
  desactive: {
    libelle: 'Désactivé',
    ton: 'alerte',
    aide: 'Accès fermé (départ de l’entreprise). Les données restent consultables par RH et ADMIN.',
  },
};

/** Transitions proposées, alignées sur la machine à états du serveur. */
const ACTIONS: Record<StatutCompte, Array<{ vers: StatutCompte; libelle: string; principal?: boolean }>> = {
  cree: [{ vers: 'invite', libelle: 'Ouvrir l’accès', principal: true }],
  invite: [
    { vers: 'suspendu', libelle: 'Suspendre' },
    { vers: 'desactive', libelle: 'Fermer l’accès' },
  ],
  actif: [
    { vers: 'suspendu', libelle: 'Suspendre' },
    { vers: 'desactive', libelle: 'Fermer l’accès' },
  ],
  suspendu: [
    { vers: 'actif', libelle: 'Rétablir l’accès', principal: true },
    { vers: 'desactive', libelle: 'Fermer l’accès' },
  ],
  desactive: [{ vers: 'invite', libelle: 'Rouvrir l’accès', principal: true }],
};

function EtiquetteStatut({ statut }: { statut: StatutCompte }) {
  const s = STATUTS[statut] ?? STATUTS.cree;
  return <Etiquette ton={s.ton}>{s.libelle}</Etiquette>;
}

function dateCourte(v: string | null | undefined) {
  if (!v) return null;
  return new Date(v).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function GestionCompte({
  fiche,
  peutAgir,
  enCours,
  onChangerStatut,
}: {
  fiche: Salarie | null;
  peutAgir: boolean;
  enCours: boolean;
  onChangerStatut: (statut: StatutCompte) => void;
}) {
  if (!fiche) {
    return (
      <p className="text-sm text-nuit/70">
        L’accès s’ouvrira une fois la fiche enregistrée.
      </p>
    );
  }

  const statut = fiche.statut_compte;
  const meta = STATUTS[statut] ?? STATUTS.cree;
  const invite = dateCourte(fiche.invite_le);
  const derniere = dateCourte(fiche.derniere_connexion_at);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <EtiquetteStatut statut={statut} />
        <span className="text-sm text-nuit/70">{meta.aide}</span>
      </div>

      <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <div className="flex gap-2">
          <dt className="text-nuit/60">Connexion&nbsp;:</dt>
          <dd className="text-nuit">compte Google {fiche.email}</dd>
        </div>
        {invite && (
          <div className="flex gap-2">
            <dt className="text-nuit/60">Invité le&nbsp;:</dt>
            <dd className="text-nuit">{invite}</dd>
          </div>
        )}
        <div className="flex gap-2">
          <dt className="text-nuit/60">Dernière connexion&nbsp;:</dt>
          <dd className="text-nuit">{derniere ?? 'jamais'}</dd>
        </div>
      </dl>

      {peutAgir && (
        <div className="flex flex-wrap gap-2 border-t border-nuit/10 pt-3">
          {(ACTIONS[statut] ?? []).map((a) =>
            a.principal ? (
              <BoutonPrincipal
                key={a.vers}
                type="button"
                disabled={enCours}
                onClick={() => onChangerStatut(a.vers)}
              >
                {enCours ? 'En cours…' : a.libelle}
              </BoutonPrincipal>
            ) : (
              <BoutonSecondaire
                key={a.vers}
                type="button"
                disabled={enCours}
                onClick={() => onChangerStatut(a.vers)}
              >
                {a.libelle}
              </BoutonSecondaire>
            ),
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Le statut RH et le type de contrat sont stockés par leur LIBELLÉ sur la fiche
 * (« Cadre », « Stage »), héritage des listes historiques. On compare donc en
 * minuscules et par préfixe, exactement comme `calculerPeriodesEssai` côté
 * serveur — une égalité stricte casserait au premier accent ou pluriel.
 */
function estCadre(f: Partial<Salarie>): boolean {
  return (f.statut_rh ?? '').trim().toLowerCase().startsWith('cadre');
}

function estStagiaire(f: Partial<Salarie>): boolean {
  const statut = (f.statut_rh ?? '').trim().toLowerCase();
  const contrat = (f.type_contrat ?? '').trim().toLowerCase();
  return statut.startsWith('stagiaire') || contrat.startsWith('stage');
}

function optionsDe(valeurs: RefValeur[] | undefined, liste: string) {
  return (valeurs ?? []).filter((v) => v.liste === liste && v.actif);
}

export function NdfSalariesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [selection, setSelection] = useState<string | null>(null);
  const [creation, setCreation] = useState(false);
  // Règle générale de l'application : un enregistrement s'ouvre en LECTURE.
  // La modification est un geste explicite, et l'enregistrement y remet fin.
  const [edition, setEdition] = useState(false);
  const [avecInactifs, setAvecInactifs] = useState(false);

  const { data: moi } = useQuery({ queryKey: ['ndf', 'moi', apercuActif()], queryFn: () => ndfApi.moi() });
  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'salaries', avecInactifs],
    queryFn: () => ndfApi.salaries.liste(avecInactifs),
    retry: false,
  });
  const { data: listes } = useQuery({
    queryKey: ['ndf', 'listes'],
    queryFn: () => ndfApi.listes.lire(),
  });

  const salaries = data?.salaries ?? [];
  const fiche = useMemo(() => salaries.find((s) => s.id === selection) ?? null, [salaries, selection]);
  // En aperçu, le serveur refuse toute écriture : proposer les boutons
  // n'aboutirait qu'à un message d'erreur. On les retire.
  // En aperçu, le serveur refuse toute écriture : proposer les boutons
  // n'aboutirait qu'à un message d'erreur. On les retire.
  const peutEcrire = peutNdf(moi, 'gerer_fiches') && !moi?.apercu;

  // Les erreurs de validation s'affichent SOUS le champ fautif ; seul ce qui
  // ne vise aucun champ (e-mail déjà pris, boucle hiérarchique) part en toast.
  const [erreurs, setErreurs] = useState<ErreursChamps>({});

  const enregistrer = useMutation({
    mutationFn: async (body: Partial<Salarie>) =>
      selection ? ndfApi.salaries.modifier(selection, body) : ndfApi.salaries.creer(body),
    onSuccess: (res) => {
      setErreurs({});
      toast.success(`Fiche de ${nomComplet(res.salarie)} enregistrée.`);
      setCreation(false);
      setEdition(false);
      setSelection(res.salarie.id);
      qc.invalidateQueries({ queryKey: ['ndf', 'salaries'] });
    },
    onError: (err) => {
      setErreurs(erreursParChamp(err));
      // Un refus qui ne vise aucun champ affiché ne s'afficherait NULLE PART :
      // l'utilisateur clique sur « Enregistrer » et rien ne se passe. C'est ce
      // qui est arrivé le 08/09/2026 en production sur les fiches sans date
      // d'entrée. Le repli garantit qu'un refus se voit toujours.
      const global = erreurGlobale(err) ?? messageSiAucunChampAffiche(err, CHAMPS_MODIFIABLES);
      if (global) toast.error(global);
      // La saisie n'est JAMAIS vidée sur erreur : le formulaire garde son état,
      // l'utilisateur corrige et renvoie.
    },
  });

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Salariés</h1>
          <p className="text-sm text-slate-500">
            Fiches RH, profils et périmètres de visibilité du module Notes de frais.
          </p>
        </div>
        {peutEcrire && (
          <button
            type="button"
            onClick={() => {
              setSelection(null);
              setCreation(true);
              setEdition(true);
              setErreurs({});
            }}
            className="min-h-11 rounded-md bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700"
          >
            Nouvelle fiche
          </button>
        )}
      </header>

      <BarreApercu observee={fiche} />

      <label className="mb-3 inline-flex items-center gap-2 text-sm text-slate-600">
        <input
          type="checkbox"
          checked={avecInactifs}
          onChange={(e) => setAvecInactifs(e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-blue-600"
        />
        Afficher les comptes fermés
      </label>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_1fr]">
        <div className="rounded-lg border border-slate-200 bg-white">
          {isLoading && <p className="p-4 text-sm text-slate-500">Chargement…</p>}
          {error && (
            <div className="p-3">
              <MessageErreur erreur={error} />
            </div>
          )}
          {!isLoading && !error && salaries.length === 0 && (
            <p className="p-4 text-sm text-slate-500">
              Aucune fiche pour le moment. Créez la première avec « Nouvelle fiche ».
            </p>
          )}
          <ul className="divide-y divide-slate-100">
            {salaries.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => {
                    setSelection(s.id);
                    setCreation(false);
                    // On retombe en lecture à chaque changement de fiche :
                    // une modification en cours ne doit pas déborder sur la suivante.
                    setEdition(false);
                    setErreurs({});
                  }}
                  className={`w-full px-4 py-3 text-left hover:bg-slate-50 ${
                    selection === s.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-slate-900">{nomComplet(s)}</span>
                    <EtiquetteStatut statut={s.statut_compte} />
                  </span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    {s.fonction || 'Fonction non renseignée'}
                  </span>
                  {/* Les étiquettes de profil ont été retirées : elles lisaient
                      des champs disparus de la fiche avec la refonte du socle
                      d'identité, et « Non déclarant » s'affichait donc sur TOUT
                      LE MONDE. Les profils se lisent dans « Utilisateurs ». */}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          {(fiche || creation) && (
            <FormulaireSalarie
              // La clé inclut le mode : entrer en modification repart de la
              // fiche enregistrée, et « Annuler » y revient. Un échec
              // d'enregistrement, lui, ne change pas le mode — donc ne remonte
              // pas le formulaire et ne perd aucune saisie.
              key={`${fiche?.id ?? 'nouvelle'}-${edition || creation ? 'modification' : 'lecture'}`}
              fiche={fiche}
              salaries={salaries}
              listes={listes?.valeurs}
              peutEcrire={peutEcrire}
              edition={edition || creation}
              creation={creation}
              enCours={enregistrer.isPending}
              erreurs={erreurs}
              onModifier={() => {
                setEdition(true);
                setErreurs({});
              }}
              onAnnuler={() => {
                setEdition(false);
                setCreation(false);
                setErreurs({});
              }}
              onEnregistrer={(body) => enregistrer.mutate(body)}
            />
          )}
          {/* Le véhicule vit à côté de la fiche, pas dedans : il se remplace au
              fil des années alors que la fiche, elle, dure. En création, la
              fiche n'a pas encore d'identifiant auquel le rattacher. */}
          {fiche && !creation && (
            <VehiculesSalarie salarieId={fiche.id} peutEcrire={peutEcrire} />
          )}
          {!fiche && !creation && (
            <p className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
              Sélectionnez une fiche à gauche pour la consulter.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function FormulaireSalarie({
  fiche,
  salaries,
  listes,
  peutEcrire,
  edition,
  creation,
  enCours,
  erreurs,
  onModifier,
  onAnnuler,
  onEnregistrer,
}: {
  fiche: Salarie | null;
  salaries: Salarie[];
  listes: RefValeur[] | undefined;
  peutEcrire: boolean;
  edition: boolean;
  creation: boolean;
  enCours: boolean;
  erreurs: ErreursChamps;
  onModifier: () => void;
  onAnnuler: () => void;
  onEnregistrer: (body: Partial<Salarie>) => void;
}) {
  const [f, setF] = useState<Partial<Salarie>>(
    fiche ?? {
      prenom: '',
      nom: '',
      email: '',
    },
  );

  function maj<K extends keyof Salarie>(cle: K, valeur: Salarie[K]) {
    setF((p) => ({ ...p, [cle]: valeur }));
  }

  // En lecture, les champs restent affichés mais inertes : on garde la même
  // mise en page, donc les mêmes repères visuels d'un mode à l'autre.
  const lectureSeule = !edition;

  // Les sections « Contrat » et « Salarié » étaient masquées pour les
  // approbateurs non salariés (arbitrage du 04/08/2026). Le drapeau qui les
  // distinguait a disparu avec la refonte du socle d'identité : depuis, la
  // condition valait toujours `true` et PLUS RIEN n'était masqué. On enlève la
  // condition morte plutôt que de faire semblant ; la rétablir demande un
  // signal réel porté par la fiche, à décider avec Valérie.

  const autres = salaries.filter((s) => s.id !== fiche?.id);

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onEnregistrer(corpsModifiable(f));
      }}
    >
      <Section titre="Identité" repliable>
        <div className="grid gap-4 sm:grid-cols-2">
          <Champ label="Civilité">
            <Selection
              value={f.civilite ?? ''}
              disabled={lectureSeule}
              onChange={(e) => maj('civilite', e.target.value || null)}
            >
              <option value="">-</option>
              {optionsDe(listes, 'civilite').map((v) => (
                <option key={v.id} value={v.libelle}>
                  {v.libelle}
                </option>
              ))}
            </Selection>
          </Champ>
          <Champ label="Matricule" erreur={erreurs.matricule} aide="Attribué automatiquement s’il est laissé vide.">
            <Saisie
              value={f.matricule ?? ''}
              disabled={lectureSeule}
              onChange={(e) => maj('matricule', e.target.value || null)}
            />
          </Champ>
          <Champ label="Prénom" requis erreur={erreurs.prenom}>
            <Saisie
              value={f.prenom ?? ''}
              required
              disabled={lectureSeule}
              onChange={(e) => maj('prenom', e.target.value)}
            />
          </Champ>
          <Champ label="Nom" requis erreur={erreurs.nom}>
            <Saisie
              value={f.nom ?? ''}
              required
              disabled={lectureSeule}
              onChange={(e) => maj('nom', e.target.value)}
            />
          </Champ>
          <Champ label="E-mail" requis erreur={erreurs.email} aide="C’est l’adresse Google utilisée pour se connecter.">
            <Saisie
              type="email"
              value={f.email ?? ''}
              required
              disabled={lectureSeule}
              onChange={(e) => maj('email', e.target.value)}
            />
          </Champ>
          <Champ label="Fonction">
            <Saisie
              value={f.fonction ?? ''}
              disabled={lectureSeule}
              onChange={(e) => maj('fonction', e.target.value || null)}
            />
          </Champ>
        </div>
      </Section>

      <Section
        titre="Données personnelles"
        teinte="corail"
        repliable
        ouvertParDefaut={false}
        description="Coordonnées privées du salarié. Le serveur les retire des réponses pour qui n’a pas le droit de les voir : un manager sans profil RH n’en reçoit aucune."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Champ label="Date de naissance" erreur={erreurs.date_naissance}>
            {lectureSeule ? (
              <ValeurCalculee>{dateFr(f.date_naissance)}</ValeurCalculee>
            ) : (
              <Saisie
                type="date"
                value={f.date_naissance ?? ''}
                onChange={(e) => maj('date_naissance', e.target.value || null)}
              />
            )}
          </Champ>
          <Champ label="Lieu de naissance">
            <Saisie
              value={f.lieu_naissance ?? ''}
              disabled={lectureSeule}
              onChange={(e) => maj('lieu_naissance', e.target.value || null)}
            />
          </Champ>
          <Champ
            label="N° de sécurité sociale"
            erreur={erreurs.num_secu}
            aide="Donnée sensible : sa consultation est journalisée."
          >
            <Saisie
              value={f.num_secu ?? ''}
              disabled={lectureSeule}
              onChange={(e) => maj('num_secu', e.target.value || null)}
            />
          </Champ>
          <Champ label="Téléphone portable" aide="Le salarié peut le modifier lui-même.">
            <Saisie
              type="tel"
              value={f.portable ?? ''}
              disabled={lectureSeule}
              onChange={(e) => maj('portable', e.target.value || null)}
            />
          </Champ>
          <Champ label="Adresse" aide="Le salarié peut la modifier lui-même.">
            <Saisie
              value={f.adresse_rue ?? ''}
              disabled={lectureSeule}
              onChange={(e) => maj('adresse_rue', e.target.value || null)}
            />
          </Champ>
          <div className="grid gap-4 sm:grid-cols-[8rem_1fr]">
            <Champ label="Code postal">
              <Saisie
                inputMode="numeric"
                value={f.adresse_cp ?? ''}
                disabled={lectureSeule}
                onChange={(e) => maj('adresse_cp', e.target.value || null)}
              />
            </Champ>
            <Champ label="Ville">
              <Saisie
                value={f.adresse_ville ?? ''}
                disabled={lectureSeule}
                onChange={(e) => maj('adresse_ville', e.target.value || null)}
              />
            </Champ>
          </div>
        </div>
      </Section>

      <p className="rounded-md border border-nuit/15 border-l-4 border-l-violet-fond bg-white p-3 text-sm text-nuit/80">
        Les droits d’accès : profil, rôle et statut du compte, se gèrent
        désormais dans <strong>Utilisateurs</strong>. Cette fiche ne décrit que
        la vie du salarié dans l’entreprise.
      </p>

      {(
        <Section titre="Contrat"
        repliable ouvertParDefaut={false}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Champ label="Date de début de contrat" requis erreur={erreurs.date_debut_contrat}>
              <Saisie
                type="date"
                value={f.date_debut_contrat ?? ''}
                disabled={lectureSeule}
                onChange={(e) => maj('date_debut_contrat', e.target.value || null)}
              />
            </Champ>
            <Champ label="Type de contrat" erreur={erreurs.type_contrat}>
              <Selection
                value={f.type_contrat ?? ''}
                disabled={lectureSeule}
                onChange={(e) => maj('type_contrat', e.target.value || null)}
              >
                <option value="">-</option>
                {optionsDe(listes, 'type_contrat').map((v) => (
                  <option key={v.id} value={v.libelle}>
                    {v.libelle}
                  </option>
                ))}
              </Selection>
            </Champ>
            <Champ
              label="Statut RH"
              aide="Détermine le calcul des périodes d’essai."
            >
              <Selection
                value={f.statut_rh ?? ''}
                disabled={lectureSeule}
                onChange={(e) => maj('statut_rh', e.target.value || null)}
              >
                <option value="">-</option>
                {optionsDe(listes, 'statut_rh').map((v) => (
                  <option key={v.id} value={v.libelle}>
                    {v.libelle}
                  </option>
                ))}
              </Selection>
            </Champ>
            <Champ label="Ancienneté">
              <ValeurCalculee>{fiche?.anciennete?.libelle ?? ''}</ValeurCalculee>
            </Champ>
            <Champ label="Fin de la 1re période d’essai">
              <ValeurCalculee
                note={
                  fiche?.periode_essai_indicative
                    ? 'Date indicative : 45 premiers jours de formation pratique, hors week-ends, jours fériés, absences et jours en centre de formation. À ajuster.'
                    : undefined
                }
              >
                {dateFr(fiche?.fin_periode_essai_1)}
              </ValeurCalculee>
            </Champ>
            <Champ label="Fin de la 2e période d’essai">
              <ValeurCalculee>{dateFr(fiche?.fin_periode_essai_2)}</ValeurCalculee>
            </Champ>
            <Champ label="Date de fin de contrat" erreur={erreurs.date_fin_contrat}>
              <Saisie
                type="date"
                value={f.date_fin_contrat ?? ''}
                disabled={lectureSeule}
                onChange={(e) => maj('date_fin_contrat', e.target.value || null)}
              />
            </Champ>
            <Champ label="Motif de départ" erreur={erreurs.motif_depart}>
              <Selection
                value={f.motif_depart ?? ''}
                disabled={lectureSeule}
                onChange={(e) => maj('motif_depart', e.target.value || null)}
              >
                <option value="">-</option>
                {optionsDe(listes, 'motif_depart').map((v) => (
                  <option key={v.id} value={v.libelle}>
                    {v.libelle}
                  </option>
                ))}
              </Selection>
            </Champ>
          </div>
        </Section>
      )}

      {/* Arrêts de travail et mi-temps thérapeutique (specs §G) : ils vivent
          sur la fiche, où la RH travaille déjà. Le composant se retire tout
          seul quand la personne n'a pas le droit de saisie RH — le serveur
          répond 403, ce qui n'est pas une panne mais le fonctionnement attendu. */}
      {fiche && <ArretsTravail salarieId={fiche.id} />}

      <Section titre="Rattachements" teinte="ciel" repliable ouvertParDefaut={false}>
        <p className="rounded-md border border-nuit/15 border-l-4 border-l-violet-fond bg-white p-3 text-sm text-nuit/80">
          Le <strong>manager</strong> se rattache sur le compte, dans{' '}
          <strong>Utilisateurs</strong> : il sert à approuver et à définir ce que
          la personne voit, et il peut désigner quelqu’un qui n’est pas salarié
 : un dirigeant, par exemple. C’est pour cela qu’il ne figure plus ici.
        </p>
        <Champ
          label="Responsable RH"
          aide="Notion RH, distincte du manager : elle désigne qui suit le dossier du salarié."
        >
          <Selection
            value={f.responsable_rh_id ?? ''}
            disabled={lectureSeule}
            onChange={(e) => maj('responsable_rh_id', e.target.value || null)}
          >
            <option value="">-</option>
            {autres.map((s) => (
              <option key={s.id} value={s.id}>
                {nomComplet(s)}
              </option>
            ))}
          </Selection>
        </Champ>
      </Section>

      {(
        <>
          {/* R-IK-05 : reprise du kilométrage déjà parcouru hors Gertrude.
              C'est le cumul, et lui seul, qui décide de la tranche du barème :
              repartir de zéro en cours d'année maintiendrait le salarié au taux
              de la première tranche, et surpaierait ses indemnités. */}
          {/* Sans ce champ, la facture de remboursement ne peut pas être émise
              dans Books : c'est le fournisseur au nom duquel elle est créée. Il
              était renseigné à l'import mais invisible ici, donc impossible à
              corriger. */}
          <Section titre="Remboursement" repliable ouvertParDefaut={false}>
            <Champ
              label="Fournisseur Zoho Books"
              erreur={erreurs.zoho_vendor_id}
              aide="Identifiant à 19 chiffres du fournisseur au nom du salarié. La facture de ses notes de frais est émise dessus."
            >
              {/* Champ TEXTE, jamais numérique : un identifiant Books à
                  19 chiffres dépasse la précision des nombres JavaScript, et
                  serait silencieusement arrondi : donc faux, sans erreur. */}
              <Saisie
                inputMode="numeric"
                value={f.zoho_vendor_id ?? ''}
                disabled={lectureSeule}
                placeholder="1234567890123456789"
                onChange={(e) => maj('zoho_vendor_id', e.target.value.replace(/\D/g, '') || null)}
              />
            </Champ>
          </Section>

          <Section titre="Reprise du kilométrage" repliable ouvertParDefaut={false}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Champ
              label="Kilomètres déjà parcourus"
              aide="Avant l’arrivée dans Gertrude, pour l’année indiquée."
            >
              <SaisieNombre
                nullable
                valeur={f.cumul_ik_initial_km ?? null}
                disabled={lectureSeule}
                onValeur={(v) => maj('cumul_ik_initial_km', v)}
              />
            </Champ>
            <Champ label="Année concernée">
              <SaisieNombre
                nullable
                valeur={f.cumul_ik_initial_annee ?? null}
                disabled={lectureSeule}
                onValeur={(v) => maj('cumul_ik_initial_annee', v)}
              />
            </Champ>
          </div>
        </Section>

          <Section titre="Congés" repliable ouvertParDefaut={false}>
            {/* Le drapeau vient EN PREMIER : décoché, le reste de la section
                n'a plus d'objet, et l'afficher quand même laisserait croire
                que la personne a des compteurs (D3.6). */}
            <Case
              label="Bénéficie des congés"
              aide="Décoché pour les mandataires sociaux : ni compteur, ni demande d’absence, ni ligne dans l’envoi paie."
              checked={f.beneficie_conges !== false}
              disabled={lectureSeule}
              onChange={(e) => maj('beneficie_conges', e.target.checked)}
            />

            {f.beneficie_conges !== false && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Champ
                  label="Modalité de temps de travail"
                  requis={estCadre(f)}
                  erreur={erreurs.modalite_temps_travail}
                  aide="Seul le forfait jour ouvre un compteur RTT."
                >
                  {/* Ce champ stocke la valeur TECHNIQUE, pas le libellé : à la
                      différence des listes historiques de cette fiche. C'est ce
                      que prévoit `ref_valeurs`, et cela permet de renommer
                      « Forfait jour » sans casser le calcul des RTT. */}
                  <Selection
                    value={f.modalite_temps_travail ?? ''}
                    disabled={lectureSeule}
                    onChange={(e) => maj('modalite_temps_travail', e.target.value || null)}
                  >
                    <option value="">-</option>
                    {optionsDe(listes, 'modalite_temps_travail').map((v) => (
                      <option key={v.id} value={v.valeur}>
                        {v.libelle}
                      </option>
                    ))}
                  </Selection>
                </Champ>

                {/* D1.7 : ne concerne que les stagiaires. Affichée en
                    permanence, la case inviterait à la cocher pour un CDI, où
                    elle n'a aucun sens. */}
                {estStagiaire(f) && (
                  <Case
                    label="Cumul des absences autorisées"
                    aide="Les 2 jours par mois de stage se cumulent sur toute la durée du stage."
                    checked={Boolean(f.cumul_absences_autorisees)}
                    disabled={lectureSeule}
                    onChange={(e) => maj('cumul_absences_autorisees', e.target.checked)}
                  />
                )}
              </div>
            )}
          </Section>

          <Section titre="Avance de frais" repliable ouvertParDefaut={false}>
            <Case
              label="Une avance de frais a été versée"
              checked={Boolean(f.avance_frais)}
              disabled={lectureSeule}
              onChange={(e) => maj('avance_frais', e.target.checked)}
            />
            {f.avance_frais && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Champ label="Montant versé" requis erreur={erreurs.avance_montant}>
                {/* `type="number"` refusait la virgule : sur un clavier
                    français, le montant devenait impossible à saisir. */}
                <SaisieNombre
                  nullable
                  valeur={f.avance_montant ?? null}
                  disabled={lectureSeule}
                  onValeur={(v) => maj('avance_montant', v)}
                />
              </Champ>
              <Champ label="Date de versement">
                <Saisie
                  type="date"
                  value={f.avance_date ?? ''}
                  disabled={lectureSeule}
                  onChange={(e) => maj('avance_date', e.target.value || null)}
                />
              </Champ>
              <Case
                label="Récupérée sur le solde de tout compte"
                checked={Boolean(f.avance_recuperee_stc)}
                disabled={lectureSeule}
                onChange={(e) => maj('avance_recuperee_stc', e.target.checked)}
              />
              </div>
            )}
          </Section>
        </>
      )}

      {/* Barre d'actions collante : sur une fiche longue, le bouton reste
          atteignable sans avoir à faire défiler jusqu'en bas. */}
      <div className="sticky bottom-0 flex justify-end gap-2 border-t border-slate-200 bg-white/95 py-3 backdrop-blur">
        {!edition && peutEcrire && (
          <button
            type="button"
            onClick={onModifier}
            className="min-h-11 rounded-md bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700"
          >
            Modifier
          </button>
        )}
        {edition && (
          <>
            <button
              type="button"
              onClick={onAnnuler}
              disabled={enCours}
              className="min-h-11 rounded-md px-4 text-sm font-medium text-slate-700 ring-1 ring-slate-300 hover:bg-slate-50 disabled:opacity-60"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={enCours}
              className="min-h-11 rounded-md bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {enCours ? 'Enregistrement…' : creation ? 'Créer la fiche' : 'Enregistrer'}
            </button>
          </>
        )}
      </div>
    </form>
  );
}
